/**
 * PortalGlobalPostbacks — Platform-level S2S postback management
 *
 * Global postbacks fire on every conversion regardless of offer/buyer.
 * Used for: network tracking, affiliate attribution, internal analytics.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Radio, Plus, Loader2, Trash2, Save, ToggleLeft, ToggleRight,
  ArrowLeft, Pencil, X, Clock, CheckCircle, AlertTriangle,
} from 'lucide-react';
import { supabaseGet, supabasePost, supabasePatch, supabaseDelete } from '@/utils/supabaseRest';
import { useAuth } from '@/contexts/AuthContext';

interface GlobalPostback {
  id: string;
  name: string;
  url: string;
  method: string;
  fire_on: string;
  custom_headers: Record<string, string>;
  custom_body: Record<string, unknown> | null;
  is_active: boolean;
  total_fired: number;
  last_fired_at: string | null;
  last_error: string | null;
  created_at: string;
}

interface PostbackLogEntry {
  id: string;
  postback_id: string;
  session_id: string | null;
  url_fired: string;
  method: string | null;
  status_code: number | null;
  success: boolean;
  error_message: string | null;
  created_at: string;
}

const MACROS = [
  '{session_id}', '{click_id}', '{affiliate_id}', '{sub_id}',
  '{payout}', '{revenue}', '{vertical}', '{offer_id}', '{buyer_id}',
  '{email}', '{phone}', '{name}', '{state}', '{decision}',
  '{intent_score}', '{monetization_score}', '{timestamp}',
];

export default function PortalGlobalPostbacks() {
  const { user } = useAuth();
  const [postbacks, setPostbacks] = useState<GlobalPostback[]>([]);
  const [logs, setLogs] = useState<PostbackLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<GlobalPostback>>({});
  const [saving, setSaving] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const [formName, setFormName] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [formMethod, setFormMethod] = useState('GET');
  const [formFireOn, setFormFireOn] = useState('conversion');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, l] = await Promise.all([
        supabaseGet('global_postbacks?order=created_at.desc'),
        supabaseGet('postback_log?order=created_at.desc&limit=50'),
      ]);
      setPostbacks(p || []);
      setLogs(l || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!formName || !formUrl || !user?.id) return;
    setCreating(true);
    try {
      await supabasePost('global_postbacks', {
        user_id: user.id, name: formName, url: formUrl,
        method: formMethod, fire_on: formFireOn, is_active: true,
      });
      setShowCreate(false); setFormName(''); setFormUrl(''); load();
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const toggle = async (p: GlobalPostback) => {
    await supabasePatch(`global_postbacks?id=eq.${p.id}`, { is_active: !p.is_active }).catch(() => {});
    setPostbacks(prev => prev.map(x => x.id === p.id ? { ...x, is_active: !x.is_active } : x));
  };

  const deletePostback = async (id: string) => {
    await supabaseDelete(`global_postbacks?id=eq.${id}`).catch(() => {});
    setPostbacks(prev => prev.filter(p => p.id !== id));
  };

  const startEdit = (p: GlobalPostback) => { setEditingId(p.id); setEditForm({ ...p }); };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const { id, total_fired, last_fired_at, last_error, created_at, ...updates } = editForm as any;
      await supabasePatch(`global_postbacks?id=eq.${editingId}`, updates);
      setPostbacks(prev => prev.map(p => p.id === editingId ? { ...p, ...updates } : p));
      setEditingId(null);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const u = (k: string, v: any) => setEditForm(f => ({ ...f, [k]: v }));

  // Editor
  if (editingId) {
    return (
      <PortalLayout>
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setEditingId(null)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
            <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save
            </Button>
          </div>
          <h1 className="text-xl font-bold text-white">{editForm.name}</h1>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5 space-y-4">
              <F label="Name" value={editForm.name} onChange={v => u('name', v)} />
              <F label="URL" value={editForm.url} onChange={v => u('url', v)} placeholder="https://tracking.com/postback?click_id={click_id}&payout={payout}" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Method</label>
                  <select value={editForm.method || 'GET'} onChange={e => u('method', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Fire On</label>
                  <select value={editForm.fire_on || 'conversion'} onChange={e => u('fire_on', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="conversion">Conversion</option>
                    <option value="lead">Lead Capture</option>
                    <option value="call">Call Connected</option>
                    <option value="all">All Events</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Available Macros</label>
                <div className="flex flex-wrap gap-1">
                  {MACROS.map(m => (
                    <button key={m} onClick={() => u('url', (editForm.url || '') + m)} className="px-1.5 py-0.5 rounded bg-slate-700 text-[10px] text-emerald-400 font-mono hover:bg-slate-600">{m}</button>
                  ))}
                </div>
              </div>
              {editForm.method === 'POST' && (
                <div>
                  <label className="text-xs text-slate-400 block mb-1">POST Body (JSON)</label>
                  <textarea value={JSON.stringify(editForm.custom_body || {}, null, 2)} onChange={e => { try { u('custom_body', JSON.parse(e.target.value)); } catch {} }} rows={5} placeholder={'{\n  "click_id": "{click_id}",\n  "payout": "{payout}"\n}'} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-emerald-400 text-xs font-mono" />
                </div>
              )}
              <div>
                <label className="text-xs text-slate-400 block mb-1">Custom Headers (JSON)</label>
                <textarea value={JSON.stringify(editForm.custom_headers || {}, null, 2)} onChange={e => { try { u('custom_headers', JSON.parse(e.target.value)); } catch {} }} rows={3} placeholder={'{\n  "X-API-Key": "abc"\n}'} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-emerald-400 text-xs font-mono" />
              </div>
              {/* URL Preview */}
              <div className="p-3 bg-slate-900 rounded-lg">
                <div className="text-[10px] text-slate-500 mb-1">Preview</div>
                <div className="text-xs text-emerald-400 font-mono break-all">{editForm.method} {editForm.url}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Radio className="h-6 w-6 text-emerald-400" /> Global Postbacks
            </h1>
            <p className="text-sm text-slate-400 mt-1">Platform-level S2S postbacks that fire on every conversion. Used for network tracking and attribution.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowLogs(!showLogs)}><Clock className="h-4 w-4 mr-1" /> {showLogs ? 'Hide' : 'Show'} Log</Button>
            <Button onClick={() => setShowCreate(!showCreate)} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4 mr-1" /> New Postback</Button>
          </div>
        </div>

        {showCreate && (
          <Card className="bg-slate-800/50 border-emerald-500/30">
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <F label="Name *" value={formName} onChange={setFormName} placeholder="Network S2S Pixel" />
                <F label="URL *" value={formUrl} onChange={setFormUrl} placeholder="https://tracking.com/postback?click_id={click_id}" />
                <div><label className="text-xs text-slate-400 block mb-1">Method</label><select value={formMethod} onChange={e => setFormMethod(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"><option value="GET">GET</option><option value="POST">POST</option></select></div>
                <div><label className="text-xs text-slate-400 block mb-1">Fire On</label><select value={formFireOn} onChange={e => setFormFireOn(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"><option value="conversion">Conversion</option><option value="lead">Lead</option><option value="call">Call</option><option value="all">All</option></select></div>
              </div>
              <div className="flex gap-2">
                <Button onClick={create} disabled={creating || !formName || !formUrl} className="bg-emerald-600 hover:bg-emerald-700">{creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Create</Button>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-emerald-400 animate-spin" /></div>
        ) : postbacks.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-8 text-center"><Radio className="h-10 w-10 text-slate-600 mx-auto mb-3" /><p className="text-sm text-slate-400">No global postbacks configured.</p></CardContent></Card>
        ) : (
          <div className="space-y-2">
            {postbacks.map(p => (
              <Card key={p.id} className={`border ${p.is_active ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-60'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <button onClick={() => toggle(p)} className="shrink-0">
                      {p.is_active ? <ToggleRight className="h-6 w-6 text-emerald-400" /> : <ToggleLeft className="h-6 w-6 text-slate-600" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{p.name}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400">{p.method}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-500/20 text-violet-400">{p.fire_on}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 font-mono truncate">{p.url}</div>
                      <div className="text-[10px] text-slate-600 mt-0.5 flex items-center gap-2">
                        <span>{p.total_fired} fired</span>
                        {p.last_fired_at && <span>Last: {new Date(p.last_fired_at).toLocaleString()}</span>}
                        {p.last_error && <span className="text-red-400 truncate max-w-[200px]">{p.last_error}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => startEdit(p)} className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-600/20 text-xs text-emerald-400 hover:bg-emerald-600/30"><Pencil className="h-3 w-3" /> Edit</button>
                      <button onClick={() => deletePostback(p.id)} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Postback Log */}
        {showLogs && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Recent Postback Log</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500 border-b border-slate-700">
                      <th className="text-left py-2 px-2">Session</th>
                      <th className="text-left py-2 px-2">URL</th>
                      <th className="text-center py-2 px-2">Status</th>
                      <th className="text-center py-2 px-2">Result</th>
                      <th className="text-right py-2 px-2">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr><td colSpan={5} className="py-4 text-center text-slate-500">No postback log entries yet</td></tr>
                    ) : logs.map(l => (
                      <tr key={l.id} className="border-b border-slate-800">
                        <td className="py-1.5 px-2 font-mono text-slate-400">{(l.session_id || '—').slice(0, 10)}</td>
                        <td className="py-1.5 px-2 text-slate-400 max-w-[250px] truncate">{l.url_fired}</td>
                        <td className="py-1.5 px-2 text-center">{l.status_code || '—'}</td>
                        <td className="py-1.5 px-2 text-center">
                          {l.success ? <CheckCircle className="h-3.5 w-3.5 text-emerald-400 inline" /> : <AlertTriangle className="h-3.5 w-3.5 text-red-400 inline" />}
                        </td>
                        <td className="py-1.5 px-2 text-right text-slate-500">{new Date(l.created_at).toLocaleTimeString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PortalLayout>
  );
}

function F({ label, value, onChange, placeholder }: { label: string; value: any; onChange: (v: string) => void; placeholder?: string }) {
  return <div><label className="text-xs text-slate-400 block mb-1">{label}</label><input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>;
}
